import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import RecordPaymentDialog from '../components/RecordPaymentDialog';
import {
  Plus,
  Trash,
  CreditCard,
  Funnel,
  Wallet,
  Eye,
  Printer,
  PencilSimple,
  EnvelopeSimple,
  FilePdf,
  Export,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import DatePickerDMY from '../components/DatePickerDMY';
import SendEmailDialog from '../components/SendEmailDialog';
import InvoiceStatusBadge from '../components/InvoiceStatusBadge';
import { openPdfInBrowser, printPdf } from '../utils/pdfUtils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDateDMY = (d) => {
  if (!d) return '';
  const p = d.split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

// Safety net: strip any legacy "[RCP-XXXX] " prefix that might still exist on
// old rows not yet touched by the migration. New payments no longer include it.
const cleanNotes = (s) => (s || '').replace(/^\[RCP-\d+\]\s*/i, '').trim();

export default function PaymentsTab({ buildingId, apartments }) {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('');

  // Record payment dialog — managed by shared RecordPaymentDialog (Phase 276).
  const [payOpen, setPayOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit payment dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editForm, setEditForm] = useState({
    amount: '',
    date: '',
    payment_source: 'cash',
    period_month: 1,
    period_year: new Date().getFullYear(),
    notes: '',
  });

  // View payment dialog
  const [viewOpen, setViewOpen] = useState(false);
  const [viewingPayment, setViewingPayment] = useState(null);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingPayment, setDeletingPayment] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailProps, setEmailProps] = useState({ pdfUrl: '', filename: '', defaultSubject: '', defaultEmail: '' });

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/buildings/${buildingId}/apartment-payments`);
      setPayments(res.data || []);
    } catch (err) {
      console.error('Failed to fetch payments', err);
    } finally {
      setLoading(false);
    }
  }, [buildingId]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  // Edit Payment: navigate into ApartmentDetails.js with ?edit_receipt=RCP so
  // the full Record-Payment-style allocation dialog opens pre-filled. This
  // keeps Create and Edit behaviors identical (same UI, same allocation logic).
  const openEditPayment = (pay) => {
    if (!pay?.apartment_id) {
      toast.error(t('error'));
      return;
    }
    if (!pay.receipt_number) {
      // Credit-applied rows (no receipt) still fall back to the simple edit.
      setEditingPayment(pay);
      setEditForm({
        amount: pay.amount || '',
        date: pay.date || '',
        payment_source: pay.payment_source || 'cash',
        period_month: pay.period_month || 1,
        period_year: pay.period_year || new Date().getFullYear(),
        notes: cleanNotes(pay.notes) || '',
      });
      setEditOpen(true);
      return;
    }
    navigate(`/apartments/${pay.apartment_id}?edit_receipt=${encodeURIComponent(pay.receipt_number)}`);
  };

  const handleEditPayment = async () => {
    if (!editingPayment) return;
    setSaving(true);
    try {
      // Notes now contain only invoice refs + optional user comments.
      // The receipt number lives in its own column (pay.receipt_number) and
      // must NOT be embedded in notes anymore.
      await axios.put(`${API}/apartment-payments/${editingPayment.payment_id}`, {
        amount: parseFloat(editForm.amount) || 0,
        date: editForm.date,
        payment_source: editForm.payment_source,
        period_month: editForm.period_month,
        period_year: editForm.period_year,
        notes: (editForm.notes || '').trim(),
      });
      toast.success(t('paymentUpdated'));
      setEditOpen(false);
      setEditingPayment(null);
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPayment) return;
    try {
      await axios.delete(`${API}/apartment-payments/${deletingPayment.payment_id}`);
      toast.success(t('paymentDeleted'));
      setDeleteOpen(false);
      setDeletingPayment(null);
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const handlePrintPayment = (pay) => {
    const w = window.open('', '_blank', 'width=400,height=500');
    if (!w) return;
    const period = pay.period_year && pay.period_month ? `${pay.period_year}-${String(pay.period_month).padStart(2, '0')}` : '-';
    w.document.write(`
      <html><head><title>${t('paymentReceipt')}</title>
      <style>body{font-family:Helvetica,Arial,sans-serif;margin:30px;font-size:13px}
      h2{margin:0 0 20px;font-size:18px;border-bottom:2px solid #000;padding-bottom:8px}
      .row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #eee}
      .label{color:#555;font-size:12px}.val{font-weight:600}
      .amt{font-size:20px;text-align:center;margin:20px 0;color:#16a34a}
      @media print{body{margin:15px}}</style></head>
      <body><h2>${t('paymentReceipt')}</h2>
      ${pay.receipt_number ? `<div class="row"><span class="label">${t('rcpNo')}</span><span class="val">${pay.receipt_number}</span></div>` : ''}
      <div class="row"><span class="label">${t('apartment')}</span><span class="val">${pay.apartment_number || '-'}</span></div>
      <div class="row"><span class="label">${t('owner')}</span><span class="val">${pay.owner_name || '-'}</span></div>
      <div class="row"><span class="label">${t('date')}</span><span class="val">${formatDateDMY(pay.date)}</span></div>
      <div class="row"><span class="label">${t('period')}</span><span class="val">${period}</span></div>
      <div class="row"><span class="label">${t('source')}</span><span class="val">${pay.payment_source === 'bank' ? t('bank') : t('cash')}</span></div>
      <div class="amt">&euro;${(pay.amount || 0).toFixed(2)}</div>
      ${cleanNotes(pay.notes) ? `<div class="row"><span class="label">${t('notes')}</span><span class="val">${cleanNotes(pay.notes)}</span></div>` : ''}
      <script>window.onload=function(){window.print()}</script></body></html>
    `);
    w.document.close();
  };

  const handlePrintFiltered = () => {
    const w = window.open('', '_blank', 'width=800,height=600');
    if (!w) return;
    const rows = filtered.map(pay => {
      const period = pay.period_year && pay.period_month ? `${pay.period_year}-${String(pay.period_month).padStart(2, '0')}` : '-';
      const cn = cleanNotes(pay.notes);
      const received = Number(pay.amount || 0);
      const allocated = Number(pay.allocated_amount || 0);
      const credit = Number(pay.unallocated_amount || 0);
      return `<tr>
        <td>${formatDateDMY(pay.date)}</td>
        <td>${pay.receipt_number || ''}</td>
        <td>${pay.apartment_number || '-'}</td>
        <td>${pay.owner_name || '-'}</td>
        <td style="text-align:right">&euro;${received.toFixed(2)}</td>
        <td style="text-align:right">&euro;${allocated.toFixed(2)}</td>
        <td style="text-align:right">${credit > 0.01 ? `€${credit.toFixed(2)}` : '—'}</td>
        <td>${pay.payment_source === 'bank' ? t('bank') : t('cash')}</td>
        <td>${period}</td>
        <td>${cn || ''}</td>
      </tr>`;
    }).join('');
    const totalReceived = filtered.reduce((s, p) => s + (p.amount || 0), 0);
    const totalAllocated = filtered.reduce((s, p) => s + (p.allocated_amount || 0), 0);
    const totalCredit = filtered.reduce((s, p) => s + (p.unallocated_amount || 0), 0);
    w.document.write(`
      <html><head><title>${t('payments')}</title>
      <style>body{font-family:Helvetica,Arial,sans-serif;margin:30px;font-size:12px}
      h2{margin:0 0 20px;font-size:18px;border-bottom:2px solid #000;padding-bottom:8px}
      table{width:100%;border-collapse:collapse}th,td{padding:6px 8px;border-bottom:1px solid #ddd;text-align:left}
      th{background:#f5f5f5;font-size:11px;font-weight:700}
      tfoot td{font-weight:700;border-top:2px solid #000}
      @media print{body{margin:15px}}</style></head>
      <body><h2>${t('payments')}</h2>
      <table><thead><tr><th>${t('date')}</th><th>${t('rcpNo')}</th><th>${t('apartment')}</th><th>${t('owner')}</th><th style="text-align:right">${t('received')}</th><th style="text-align:right">${t('allocated')}</th><th style="text-align:right">${t('availableCredit')}</th><th>${t('source')}</th><th>${t('period')}</th><th>${t('notes')}</th></tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr><td colspan="4">${t('total')}</td><td style="text-align:right">&euro;${totalReceived.toFixed(2)}</td><td style="text-align:right">&euro;${totalAllocated.toFixed(2)}</td><td style="text-align:right">&euro;${totalCredit.toFixed(2)}</td><td colspan="3"></td></tr></tfoot>
      </table>
      <script>window.onload=function(){window.print()}</script></body></html>
    `);
    w.document.close();
  };

  const handleExportPdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(t('payments'), 14, 18);
    doc.setFontSize(10);
    doc.text(`${filtered.length} ${t('payments')}`, 14, 25);

    const tableData = filtered.map(pay => {
      const period = pay.period_year && pay.period_month ? `${pay.period_year}-${String(pay.period_month).padStart(2, '0')}` : '-';
      const received = Number(pay.amount || 0);
      const allocated = Number(pay.allocated_amount || 0);
      const credit = Number(pay.unallocated_amount || 0);
      return [
        formatDateDMY(pay.date),
        pay.receipt_number || '',
        pay.apartment_number || '-',
        pay.owner_name || '-',
        `€${received.toFixed(2)}`,
        `€${allocated.toFixed(2)}`,
        credit > 0.01 ? `€${credit.toFixed(2)}` : '-',
        pay.payment_source === 'bank' ? t('bank') : t('cash'),
        period,
      ];
    });
    const totalReceived = filtered.reduce((s, p) => s + (p.amount || 0), 0);
    const totalAllocated = filtered.reduce((s, p) => s + (p.allocated_amount || 0), 0);
    const totalCredit = filtered.reduce((s, p) => s + (p.unallocated_amount || 0), 0);
    tableData.push([t('total'), '', '', '', `€${totalReceived.toFixed(2)}`, `€${totalAllocated.toFixed(2)}`, `€${totalCredit.toFixed(2)}`, '', '']);

    autoTable(doc, {
      startY: 30,
      head: [[t('date'), t('rcpNo'), t('apartment'), t('owner'), t('received'), t('allocated'), t('availableCredit'), t('source'), t('period')]],
      body: tableData,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [24, 24, 43], textColor: 255 },
    });

    doc.save('payments.pdf');
  };

  // Filter
  const filtered = payments.filter(p => {
    if (sourceFilter !== 'all' && p.payment_source !== sourceFilter) return false;
    if (periodFilter) {
      const pPeriod = p.period_year && p.period_month ? `${p.period_year}-${String(p.period_month).padStart(2, '0')}` : '';
      if (pPeriod !== periodFilter) return false;
    }
    return true;
  });

  const totalPayments = filtered.reduce((s, p) => s + (p.amount || 0), 0);
  const cashTotal = filtered.filter(p => p.payment_source === 'cash').reduce((s, p) => s + (p.amount || 0), 0);
  const bankTotal = filtered.filter(p => p.payment_source === 'bank').reduce((s, p) => s + (p.amount || 0), 0);

  const periods = [...new Set(payments.map(p => {
    if (p.period_year && p.period_month) return `${p.period_year}-${String(p.period_month).padStart(2, '0')}`;
    return null;
  }).filter(Boolean))].sort().reverse();

  return (
    <div className="space-y-4" data-testid="payments-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-base font-bold text-zinc-800 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
          <CreditCard size={20} weight="fill" className="text-emerald-600" />
          {t('payments')}
        </h3>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-none text-xs h-8 gap-1" disabled={filtered.length === 0} data-testid="export-payments-btn">
                  <Export size={14} /> {t('export')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none">
                <DropdownMenuItem onClick={handlePrintFiltered} data-testid="print-payments-btn">
                  <Printer size={14} className="mr-2" /> {t('printPdf')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportPdf} data-testid="export-pdf-payments-btn">
                  <FilePdf size={14} className="mr-2" /> {t('exportPdf')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              onClick={() => setPayOpen(true)}
              className="bg-zinc-950 rounded-none text-xs h-8 gap-1"
              data-testid="record-payment-btn"
            >
              <Plus size={14} /> {t('recordPayment')}
            </Button>
          </div>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-zinc-200 p-3">
          <div className="text-xs text-zinc-500">{t('totalPayments')}</div>
          <div className="text-lg font-bold font-mono text-zinc-900">&euro;{totalPayments.toFixed(2)}</div>
        </div>
        <div className="bg-white border border-zinc-200 p-3">
          <div className="text-xs text-zinc-500">{t('cashPayments')}</div>
          <div className="text-lg font-bold font-mono text-emerald-700">&euro;{cashTotal.toFixed(2)}</div>
        </div>
        <div className="bg-white border border-zinc-200 p-3">
          <div className="text-xs text-zinc-500">{t('bankPayments')}</div>
          <div className="text-lg font-bold font-mono text-blue-700">&euro;{bankTotal.toFixed(2)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap bg-zinc-50 border border-zinc-200 p-2">
        <Funnel size={14} className="text-zinc-400" />
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-[140px] h-7 text-xs rounded-none border-zinc-300" data-testid="payment-source-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">{t('allSources')}</SelectItem>
            <SelectItem value="cash">{t('cash')}</SelectItem>
            <SelectItem value="bank">{t('bank')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={periodFilter || '__all__'} onValueChange={v => setPeriodFilter(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-[130px] h-7 text-xs rounded-none border-zinc-300" data-testid="payment-period-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="__all__">{t('allPeriods')}</SelectItem>
            {periods.map(p => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">{t('loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-400">
          <Wallet size={32} className="mx-auto mb-2 opacity-40" />
          <p>{t('noPayments')}</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <table className="w-full text-xs" data-testid="payments-table">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200 text-left">
                <th className="px-3 py-2 font-semibold text-zinc-600">{t('date')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600">{t('rcpNo')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600">{t('apartment')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600">{t('owner')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600">{t('period')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600 text-right">{t('received')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600 text-right">{t('allocated')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600 text-right">{t('availableCredit')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600">{t('source')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600">{t('status')}</th>
                <th className="px-3 py-2 font-semibold text-zinc-600">{t('notes')}</th>
                {isAdmin && <th className="px-3 py-2 font-semibold text-zinc-600 text-center">{t('actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(pay => {
                const sourceLabel = pay.payment_source === 'bank' ? t('bank') : pay.payment_source === 'credit' ? t('credit') : t('cash');
                const sourceColor = pay.payment_source === 'bank' ? 'bg-blue-50 text-blue-700 border-blue-200' : pay.payment_source === 'credit' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200';
                const cleanedNotes = cleanNotes(pay.notes);
                const received = Number(pay.amount || 0);
                const allocated = Number(pay.allocated_amount || 0);
                const credit = Number(pay.unallocated_amount || 0);
                const hasCredit = credit > 0.01;
                return (
                <tr key={pay.payment_id} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                  <td className="px-3 py-2 font-mono text-zinc-600">{formatDateDMY(pay.date)}</td>
                  <td className="px-3 py-2 font-mono text-zinc-700 font-semibold" data-testid={`pay-rcp-${pay.payment_id}`}>{pay.receipt_number || ''}</td>
                  <td className="px-3 py-2 font-mono font-semibold">{pay.apartment_number || '-'}</td>
                  <td className="px-3 py-2 text-zinc-600">{pay.owner_name || '-'}</td>
                  <td className="px-3 py-2 font-mono text-zinc-600">
                    {pay.period_year && pay.period_month ? `${pay.period_year}-${String(pay.period_month).padStart(2, '0')}` : '-'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700" data-testid={`pay-received-${pay.payment_id}`}>&euro;{received.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-700" data-testid={`pay-allocated-${pay.payment_id}`}>&euro;{allocated.toFixed(2)}</td>
                  <td className={`px-3 py-2 text-right font-mono ${hasCredit ? 'font-semibold text-indigo-700' : 'text-zinc-400'}`} data-testid={`pay-credit-${pay.payment_id}`}>
                    {hasCredit ? `€${credit.toFixed(2)}` : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 border ${sourceColor}`}>{sourceLabel}</span>
                  </td>
                  <td className="px-3 py-2">
                    <InvoiceStatusBadge status="paid" testId={`pay-status-${pay.payment_id}`} />
                  </td>
                  <td className="px-3 py-2 text-zinc-500 max-w-[150px] truncate" title={cleanedNotes}>{cleanedNotes || ''}</td>
                  {isAdmin && (
                    <td className="px-3 py-2 text-center">
                      <div className="flex justify-center gap-0.5">
                        <button onClick={() => openPdfInBrowser(`/apartment-payments/${pay.payment_id}/pdf`)} className="p-1 hover:bg-blue-50 text-zinc-400 hover:text-blue-600 transition-colors" title={t('view')} data-testid={`view-payment-${pay.payment_id}`}><Eye size={14} /></button>
                        <button onClick={() => printPdf(`/apartment-payments/${pay.payment_id}/pdf`)} className="p-1 hover:bg-blue-50 text-zinc-400 hover:text-blue-600 transition-colors" title={t('printPdf')} data-testid={`print-payment-${pay.payment_id}`}><Printer size={14} /></button>
                        <button onClick={() => {
                          setEmailProps({
                            pdfUrl: `/api/apartment-payments/${pay.payment_id}/pdf`,
                            filename: `Receipt_${pay.apartment_number}_${pay.date}.pdf`,
                            defaultSubject: `Payment Receipt - Apt ${pay.apartment_number}`,
                            defaultEmail: '',
                          });
                          setEmailOpen(true);
                        }} className="p-1 hover:bg-purple-50 text-zinc-400 hover:text-purple-600 transition-colors" title={t('sendByEmail')} data-testid={`email-payment-${pay.payment_id}`}><EnvelopeSimple size={14} /></button>
                        <button onClick={() => openEditPayment(pay)} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('edit')} data-testid={`edit-payment-${pay.payment_id}`}><PencilSimple size={14} /></button>
                        <button onClick={() => { setDeletingPayment(pay); setDeleteOpen(true); }} className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors" title={t('delete')} data-testid={`delete-payment-${pay.payment_id}`}><Trash size={14} /></button>
                      </div>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* View Payment Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-sm rounded-none">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('paymentDetails')}</DialogTitle>
            <DialogDescription className="sr-only">{t('paymentDetails')}</DialogDescription>
          </DialogHeader>
          {viewingPayment && (
            <div className="space-y-3" data-testid="view-payment-details">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-zinc-400 text-xs">{t('apartment')}</span><div className="font-mono font-semibold">{viewingPayment.apartment_number || '-'}</div></div>
                <div><span className="text-zinc-400 text-xs">{t('owner')}</span><div className="font-semibold">{viewingPayment.owner_name || '-'}</div></div>
                <div><span className="text-zinc-400 text-xs">{t('date')}</span><div className="font-mono">{formatDateDMY(viewingPayment.date)}</div></div>
                {viewingPayment.receipt_number && (
                  <div><span className="text-zinc-400 text-xs">{t('rcpNo')}</span><div className="font-mono font-semibold">{viewingPayment.receipt_number}</div></div>
                )}
                <div><span className="text-zinc-400 text-xs">{t('source')}</span><div>{viewingPayment.paid_from_reserve_fund ? t('reserveFund') : (viewingPayment.payment_source === 'bank' ? t('bank') : t('cash'))}</div></div>
                <div><span className="text-zinc-400 text-xs">{t('period')}</span><div className="font-mono">{viewingPayment.period_year && viewingPayment.period_month ? `${viewingPayment.period_year}-${String(viewingPayment.period_month).padStart(2, '0')}` : '-'}</div></div>
              </div>
              <div className="text-center py-3 border-t border-b border-zinc-200">
                <div className="text-xs text-zinc-400">{t('amount')}</div>
                <div className="text-2xl font-bold font-mono text-emerald-700">&euro;{(viewingPayment.amount || 0).toFixed(2)}</div>
              </div>
              {cleanNotes(viewingPayment.notes) && (
                <div><span className="text-zinc-400 text-xs">{t('notes')}</span><div className="text-sm">{cleanNotes(viewingPayment.notes)}</div></div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewOpen(false)} className="rounded-none">{t('close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 276 — Shared Record Payment dialog. Same component as
            ApartmentDetails so the UI / fields / logic stay perfectly
            consistent across both screens. */}
      <RecordPaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        buildingId={buildingId}
        apartments={apartments}
        onSaved={fetchPayments}
      />

      {/* Edit Payment Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('editPayment')}</DialogTitle>
            <DialogDescription className="sr-only">{t('editPayment')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4" data-testid="edit-payment-form">
            {editingPayment && (
              <div className="text-xs bg-zinc-50 border border-zinc-200 px-3 py-2">
                {t('apartment')}: <span className="font-mono font-semibold">{editingPayment.apartment_number}</span> — {editingPayment.owner_name || '-'}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-500">{t('amount')} (&euro;)</Label>
                <Input type="number" step="0.01" min="0" value={editForm.amount} onChange={e => setEditForm({ ...editForm, amount: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="edit-payment-amount" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">{t('date')}</Label>
                <DatePickerDMY value={editForm.date} onChange={v => setEditForm({ ...editForm, date: v })} className="mt-1 font-mono" data-testid="edit-payment-date" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-zinc-500">{t('source')}</Label>
                <Select value={editForm.payment_source} onValueChange={v => setEditForm({ ...editForm, payment_source: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="edit-payment-source"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="cash">{t('cash')}</SelectItem>
                    <SelectItem value="bank">{t('bank')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">{t('month')}</Label>
                <Select value={String(editForm.period_month)} onValueChange={v => setEditForm({ ...editForm, period_month: parseInt(v) })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="edit-payment-month"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {Array.from({ length: 12 }, (_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{t(`month_${i + 1}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">{t('year')}</Label>
                <Input type="number" value={editForm.period_year} onChange={e => setEditForm({ ...editForm, period_year: parseInt(e.target.value) || new Date().getFullYear() })} className="rounded-none mt-1 font-mono" data-testid="edit-payment-year" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">{t('notes')}</Label>
              <Input value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} className="rounded-none mt-1" data-testid="edit-payment-notes" />
            </div>
            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-none">{t('cancel')}</Button>
              <Button onClick={handleEditPayment} disabled={saving} className="bg-zinc-950 rounded-none" data-testid="edit-payment-submit">
                {saving ? t('saving') : t('save')}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Payment Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-sm rounded-none">
          <DialogHeader>
            <DialogTitle>{t('deletePayment')}</DialogTitle>
            <DialogDescription className="sr-only">{t('confirmDeletePayment')}</DialogDescription>
          </DialogHeader>
          {deletingPayment && (
            <div className="space-y-2">
              <p className="text-sm text-zinc-600">{t('confirmDeletePayment')}</p>
              <div className="bg-zinc-50 border border-zinc-200 p-3 text-xs font-mono space-y-1">
                <div>{t('apartment')}: {deletingPayment.apartment_number}</div>
                <div>{t('amount')}: &euro;{(deletingPayment.amount || 0).toFixed(2)}</div>
                <div>{t('date')}: {formatDateDMY(deletingPayment.date)}</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} className="rounded-none">{t('cancel')}</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="confirm-delete-payment-btn">{t('delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SendEmailDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        pdfUrl={emailProps.pdfUrl}
        filename={emailProps.filename}
        defaultSubject={emailProps.defaultSubject}
        defaultEmail={emailProps.defaultEmail}
        buildingId={buildingId}
      />
    </div>
  );
}
